/**
 * The one calculation behind every eBay price in the app.
 *
 * Both the number on the card and the number in the breakdown come through
 * here, so they can never disagree. The card face always uses DEFAULT_PREFS -
 * the average of the three most recent sales for the tier the card is actually
 * in. One rule everywhere means a collection total means something and two
 * cards can be compared. The breakdown lets someone move the window and the
 * method, but that is exploration; it does not change what the card shows.
 *
 * Three sales is a small number, and that is the whole reason the hold-out
 * rules matter. On Base Set Charizard, leaving the flagged sales in moves the
 * three-sale average from $677 to $508. Widen to ten sales and the same
 * exclusions move it by three percent. The narrower the window, the more one
 * bad comp is worth, so the cleaning has to happen before the three are picked.
 */

import { slabInTitle, leadingSlab } from './slabInTitle';

/** What the card face shows, always. */
export const DEFAULT_PREFS = Object.freeze({
  count: 3,
  days: null,
  method: 'avg',
  holdOut: true,
});

export const METHODS = [
  { id: 'avg', label: 'Average' },
  { id: 'med', label: 'Median' },
  { id: 'top3', label: 'Top 3' },
  { id: 'low3', label: 'Bottom 3' },
];

export const COUNT_WINDOWS = [3, 5, 10, 25];
export const DAY_WINDOWS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
  { days: 0, label: 'All time' },
];

/** Why a sale is not counted. Null means it is. */
export const HOLD = {
  FLAGGED: 'flagged', SLAB: 'slab', REPORTED: 'reported',
  MISGRADED: 'misgraded', EXCLUDED: 'excluded',
};

/**
 * Held-out sets, normalised.
 *
 * Callers pass either `{ reported, excluded }` or, from before excludes
 * existed, a bare Set of reported ids. Both are still in the codebase and both
 * mean the same thing to everything downstream.
 */
function heldSets(held) {
  if (!held) return { reported: null, excluded: null };
  if (held instanceof Set) return { reported: held, excluded: null };
  return { reported: held.reported || null, excluded: held.excluded || null };
}

const RAW_CONDITIONS = new Set([
  'NEAR_MINT', 'LIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED',
]);

// Rows already in the cache were written by an older pass that kept one
// `condition` field for everything: "nm" for a raw card, "PSA 9" for a slab.
// Newer rows carry company and grade separately. Both are in the table right
// now and neither is going to be rewritten, so the reader normalises.
const SHORT_CONDITION = {
  nm: 'NEAR_MINT',
  lp: 'LIGHTLY_PLAYED',
  mp: 'MODERATELY_PLAYED',
  hp: 'HEAVILY_PLAYED',
  dmg: 'DAMAGED',
};

/** Canonical tier key for a sale, or null when it cannot be placed. */
export function tierOf(sale) {
  if (!sale || sale.isAggregate) return null;

  // Explicit company plus grade. Unambiguous, so it wins.
  const company = String(sale.company || '').trim().toUpperCase();
  if (company && company !== 'RAW') {
    const grade = parseFloat(sale.grade_number ?? sale.grade);
    if (!Number.isFinite(grade)) return null;
    return `${company}_${String(grade).replace('.', '_')}`;
  }

  const condition = String(sale.condition || '').trim();
  if (!condition) return null;

  const short = SHORT_CONDITION[condition.toLowerCase()];
  if (short) return short;

  const long = condition.toUpperCase().replace(/\s+/g, '_');
  if (RAW_CONDITIONS.has(long)) return long;

  // "PSA 9", "CGC 7.5" — a grade hiding in the condition field.
  const slab = condition.match(/^([A-Za-z]+)\s*([\d.]+)$/);
  if (slab) {
    const grade = parseFloat(slab[2]);
    if (!Number.isFinite(grade)) return null;
    return `${slab[1].toUpperCase()}_${String(grade).replace('.', '_')}`;
  }

  return null;
}

/**
 * Does this sale belong to the tier the row is showing?
 *
 * Tier keys arrive as the ladder writes them: "NEAR_MINT" for a raw condition,
 * "PSA_10" or "BGS_9_5" for a slab.
 */
export function matchesTier(sale, tierKey) {
  if (!tierKey) return false;
  return tierOf(sale) === String(tierKey).toUpperCase();
}

/** Individual sales for one tier, newest first. Aggregates are not sales. */
export function salesForTier(evidence, tierKey) {
  return (evidence || [])
    .filter((s) => s && !s.isAggregate && s.price > 0 && matchesTier(s, tierKey))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

/**
 * @param {Object} sale
 * @param {{reported:Set<string>, excluded:Set<string>}|Set<string>} [held]
 *        eBay item ids held out for this viewer, by kind
 * @returns {string|null} a HOLD reason, or null when the sale counts
 */
export function holdReason(sale, held) {
  if (!sale) return null;

  const { reported, excluded } = heldSets(held);
  const id = itemIdOf(sale);

  // Your own exclude is checked first and named separately. "I do not want this
  // one" and "this listing is wrong" are different statements, and a row struck
  // through with the wrong label invites the wrong correction.
  if (excluded && excluded.size && excluded.has(id)) return HOLD.EXCLUDED;
  if (sale.anomalyFlag) return HOLD.FLAGGED;
  if (reported && reported.size && reported.has(id)) return HOLD.REPORTED;
  // Only meaningful on rows the grader column already calls raw; a slab that
  // declared its grade is in its own tier and belongs there.
  if ((!sale.company || sale.company === 'raw') && slabInTitle(sale.title).graded) return HOLD.SLAB;

  // The grader column says one thing and the title leads with another.
  //
  // The vendor parses the grade out of the title, so a seller who writes a
  // hopeful cross-grade into it gets believed. An SGC 9.5 titled "... Graded
  // 9.5 (PSA 10?)" arrived as a PSA 10 at $1,408 and sat in a tier whose two
  // genuine sales were $22,500 and $19,433. Sellers lead with the slab they
  // actually have, so where the first pair in the title disagrees with the
  // column, the column is the one that is wrong.
  const assigned = String(sale.company || '').toUpperCase();
  if (assigned && assigned !== 'RAW') {
    const lead = leadingSlab(sale.title);
    const grade = parseFloat(sale.grade_number ?? sale.grade);
    if (lead && Number.isFinite(grade)
      && (lead.company !== assigned || lead.grade !== grade)) return HOLD.MISGRADED;
  }

  return null;
}

/** eBay's own item id, pulled off the listing URL. Survives changing vendors. */
export function itemIdOf(sale) {
  const match = String(sale?.url || '').match(/\/itm\/(\d+)/);
  return match ? match[1] : (sale?.sourceItemId || sale?.url || null);
}

const daysBetween = (date, now) => {
  const then = Date.parse(date);
  if (!Number.isFinite(then)) return null;
  return Math.floor((now - then) / 86400000);
};

const mean = (values) => (values.length
  ? values.reduce((sum, v) => sum + v, 0) / values.length
  : null);

const middle = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

function reduce(prices, method) {
  if (!prices.length) return null;
  if (method === 'med') return middle(prices);
  const sorted = [...prices].sort((a, b) => a - b);
  if (method === 'top3') return mean(sorted.slice(-3));
  if (method === 'low3') return mean(sorted.slice(0, 3));
  return mean(prices);
}

/**
 * Work a price out of one tier's sales.
 *
 * @returns {{
 *   value: number|null, counted: number, heldOut: number,
 *   basis: string, rows: Array<{sale, held: string|null, counted: boolean}>
 * }}
 */
export function priceFrom(sales, prefs = DEFAULT_PREFS, held = null, now = Date.now()) {
  const { count, days, method, holdOut } = { ...DEFAULT_PREFS, ...(prefs || {}) };

  const marked = (sales || []).map((sale) => ({ sale, held: holdReason(sale, held) }));
  const eligible = holdOut ? marked.filter((r) => !r.held) : marked;

  let chosen;
  if (days != null) {
    chosen = days === 0
      ? eligible
      : eligible.filter((r) => {
        const age = daysBetween(r.sale.date, now);
        // An undated sale is almost always a recent one the vendor has not
        // stamped yet. Dropping it would silently shrink a three-sale window.
        return age == null || age <= days;
      });
  } else {
    chosen = eligible.slice(0, count);
  }

  const keep = new Set(chosen.map((r) => r.sale));
  const value = reduce(chosen.map((r) => r.sale.price), method);

  return {
    value: value == null ? null : Math.round(value * 100) / 100,
    counted: chosen.length,
    heldOut: marked.filter((r) => r.held).length,
    basis: describe({ count, days, method, holdOut }, chosen.length),
    rows: marked.map((r) => ({ ...r, counted: keep.has(r.sale) })),
  };
}

function describe(prefs, n) {
  const how = prefs.method === 'med' ? 'Median'
    : prefs.method === 'top3' ? 'Average of the top 3'
      : prefs.method === 'low3' ? 'Average of the bottom 3'
        : 'Average';
  const of = prefs.days != null
    ? (prefs.days === 0
      ? `all ${n} sales`
      : `${n} ${n === 1 ? 'sale' : 'sales'} in the last ${prefs.days === 365 ? 'year' : `${prefs.days} days`}`)
    : `the last ${n} ${n === 1 ? 'sale' : 'sales'}`;
  return `${how} of ${of}${prefs.holdOut ? ', questionable held out' : ', everything counted'}`;
}

/** The number the card face shows. Never takes a preference. */
export function headlinePrice(evidence, tierKey, held = null) {
  return priceFrom(salesForTier(evidence, tierKey), DEFAULT_PREFS, held);
}

const priceMath = {
  headlinePrice, priceFrom, salesForTier, matchesTier, tierOf, holdReason, itemIdOf, DEFAULT_PREFS,
};

export default priceMath;
